import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import McpKeySettings from './McpKeySettings';
import PromptSettings from './PromptSettings';

const TABS = [
  { id: 'mcp-key', label: 'MCP API Key', path: '/settings/mcp-key' },
  { id: 'prompts', label: 'Custom Prompts', path: '/settings/prompts' },
];

function Settings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const availableTabs = TABS.filter(t => {
    if (t.id === 'prompts') return user?.is_admin || user?.can_create_prompts;
    return true;
  });

  const currentTab = TABS.find(t => location.pathname.startsWith(t.path))?.id;
  const activeTab = availableTabs.find(t => t.id === currentTab)?.id || 'mcp-key';

  // If user is on /settings/prompts but doesn't have access, redirect to /settings
  React.useEffect(() => {
    if (location.pathname === '/settings/prompts' && !user?.is_admin && !user?.can_create_prompts) {
      navigate('/settings');
    }
  }, [location.pathname, user, navigate]);

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-[#f8f5fa] via-[#faf8fc] to-[#f5f0f8]">
      {/* Fixed header */}
      <div className="shrink-0 px-4 sm:px-6 pt-4 sm:pt-5 pb-0 max-w-2xl w-full mx-auto">
        <div className="flex items-center gap-2.5 mb-4">
          <button
            onClick={() => navigate('/app')}
            className="flex items-center justify-center p-1 rounded-md text-kaleido-gray-400 hover:bg-kaleido-purple/10 hover:text-kaleido-purple transition-all duration-150"
            aria-label="Back"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <div>
            <h1 className="text-base font-semibold text-kaleido-gray-900 font-sans leading-tight">Settings</h1>
            <p className="text-xs text-kaleido-gray-400">{user?.email}</p>
          </div>
        </div>

        {user?.is_admin && (
          <button
            onClick={() => navigate('/admin')}
            className="
              mb-4 flex items-center gap-2 px-3 py-2
              rounded-lg border border-kaleido-purple/15
              bg-white/50 text-kaleido-purple text-xs font-medium
              hover:bg-kaleido-purple/8 transition-all duration-150
            "
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            Admin Console
          </button>
        )}

        {/* Tab bar */}
        <div className="flex gap-0.5 border-b border-kaleido-gray-200/60">
          {availableTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={`
                px-3 py-1.5 text-xs font-medium transition-all duration-150 border-b-2 -mb-px
                ${activeTab === tab.id
                  ? 'text-kaleido-purple border-kaleido-purple'
                  : 'text-kaleido-gray-400 border-transparent hover:text-kaleido-gray-600 hover:border-kaleido-gray-300'}
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
        <div className="max-w-2xl mx-auto">
          {activeTab === 'mcp-key' && <McpKeySettings />}
          {activeTab === 'prompts' && <PromptSettings />}
        </div>
      </div>
    </div>
  );
}

export default Settings;
